import type { PurchaseOrderLine, PurchaseOrderStatus } from "@shared/types";

/**
 * Checks whether any line items have unprinted labels.
 * A label is considered "unprinted" when its fulfillment status is "LabelCreated",
 * it has no `labelPrintedDate`, and it has a `labelCreatedDate`.
 *
 * @param lineItems - Array of purchase order line items to check
 * @returns `true` if at least one line item has an unprinted label
 */
export const hasUnprintedLabels = (lineItems: PurchaseOrderLine[]): boolean => {
  return lineItems.some(({ fulfillmentStatus, labelCreatedDate, labelPrintedDate }) => {
    return fulfillmentStatus === "LabelCreated" && !labelPrintedDate && Boolean(labelCreatedDate);
  });
};

interface LabelNotPrintedWarningInput {
  beanzConnectLabelPrintEnabled?: boolean;
  orderStatus?: PurchaseOrderStatus;
  lineItems?: PurchaseOrderLine[];
}

/**
 * Determines whether the "label not printed" warning should be displayed for an order.
 * Combines the feature flag check, order status check, and unprinted label check.
 *
 * @param order - Object containing the relevant order fields
 * @returns `true` if the warning should be shown
 */
export const shouldShowLabelNotPrintedWarning = ({
  beanzConnectLabelPrintEnabled,
  orderStatus,
  lineItems,
}: LabelNotPrintedWarningInput): boolean => {
  if (!beanzConnectLabelPrintEnabled || orderStatus !== "Fulfilled" || !lineItems?.length) {
    return false;
  }
  return hasUnprintedLabels(lineItems);
};
